import { createContext, useContext, useMemo, useState, useEffect, useCallback } from 'react'
import { ALLOWED_DIFFS, PUZZLE_IMAGE_PATHS } from '../constants/puzzle'
import { getDailyImage, getCoverImage, getImageMetadata } from '../utils/puzzleImageResolver'
import { getItem, setItem } from '@utils/storage'

/**
 * PuzzleContext
 * Single source of truth for puzzle UI flow and settings.
 *
 * State shape:
 * - stage: 'start' | 'game' | 'end'
 * - difficulty: 'easy' | 'medium' | 'hard' (persisted as user preference)
 * - gameSnapshot: Inmutable snapshot created at startGame(), includes:
 *   - imageId: Canonical image identifier (e. g., 'd15', 'cover')
 *   - imageSrc: Full image path
 *   - metadata: Image metadata (i18n keys, credits, etc.) - Future
 *   - difficulty: Difficulty selected at game start
 *   - timeStamp: Game start time (ms since epoch)
 *
 * Notes:
 * - We do NOT persist 'stage' for MVP (no resume-in-the-middle).
 * - Difficulty is persisted as a user preference only.
 * - gameSnapshot is created at game start and remains unchanged until exitToStart().
 */
export const PuzzleContext = createContext(null)

const DIFF_STORAGE_KEY = 'puzzle:difficulty'

export function PuzzleProvider ({ children }) {
  const [stage, setStage] = useState('start')
  const [gameSnapshot, setGameSnapshot] = useState(null) // { difficulty, imageId, imageSrc, timeStamp }

  const [difficulty, setDifficulty] = useState(() => {
    const saved = getItem(DIFF_STORAGE_KEY)
    return ALLOWED_DIFFS.has(saved) ? saved : 'easy'
  })

  useEffect(() => {
    if (ALLOWED_DIFFS.has(difficulty)) {
      setItem(DIFF_STORAGE_KEY, difficulty)
    }
  }, [difficulty])

  /**
   * Start a new game
   * Creates immutable gameSnapshot with image resolved at runtime
   *
   * @param {string} d - Difficulty ('easy' | 'medium' | 'hard')
   * @param {string} imageType - Image type ('daily' | 'cover' | custom)
   */
  const startGame = useCallback((d = 'easy', imageType = 'daily') => {
    const validDifficulty = ALLOWED_DIFFS.has(d) ? d : 'easy'

    // Resolve image at runtime

    let imageData
    let metadata

    if (imageType === 'daily') {
      // Get today's image (runtime evaluation)
      imageData = getDailyImage()
      metadata = getImageMetadata(imageData.id)
    } else if (imageType === 'cover') {
      // Use cover explicitly
      imageData = getCoverImage()
      metadata = getImageMetadata('cover')
    } else {
      // Future: custom images, campaign-specific, etc.
      imageData = {
        id: imageType,
        src: PUZZLE_IMAGE_PATHS[imageType] || PUZZLE_IMAGE_PATHS.cover
      }
      metadata = getImageMetadata(imageType)
    }

    // Create immutable snapshot
    setDifficulty(validDifficulty)
    setGameSnapshot({
      difficulty: validDifficulty,
      imageId: imageData.id,
      imageSrc: imageData.src,
      metadata,
      timeStamp: Date.now()
    })
    setStage('game')
  }, [])

  const exitToStart = useCallback(() => {
    setStage('start')
    setGameSnapshot(null)
  }, [])

  const finishGame = useCallback(() => {
    setStage('end')
  }, [])

  const value = useMemo(() => ({
    stage,
    setStage,
    difficulty,
    setDifficulty,
    gameSnapshot,
    // actions
    startGame,
    exitToStart,
    finishGame
  }), [stage, difficulty, gameSnapshot, startGame, exitToStart, finishGame])

  return (
    <PuzzleContext.Provider value={value}>
      {children}
    </PuzzleContext.Provider>
  )
}

/**
 * Hook to consume the puzzle context.
 * Throws if used outside of <PuzzleProvider>.
 */
export function usePuzzle () {
  const ctx = useContext(PuzzleContext)
  if (!ctx) {
    throw new Error('usePuzzle must be used within a PuzzleProvider')
  }
  return ctx
}
